import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { fetchSuppliers, type Supplier } from '@/services/suppliers';
import { fetchVariants, type VariantOption } from '@/services/variants';
import { savePurchase } from '@/services/purchases';

interface LineItem {
  variantId: string;
  quantity: string;
  unitCost: string;
  retailPrice: string;
}

interface PurchaseModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function PurchaseModal({ onClose, onSaved }: PurchaseModalProps) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [variants, setVariants] = useState<VariantOption[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineItem[]>([
    { variantId: '', quantity: '1', unitCost: '', retailPrice: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSuppliers().then((r) => {
      setSuppliers(r.data);
      if (r.data[0]) setSupplierId(r.data[0].id);
    });
    fetchVariants().then((r) => setVariants(r.data));
  }, []);

  const total = useMemo(
    () =>
      lines.reduce((sum, l) => {
        const qty = Number(l.quantity) || 0;
        const cost = Number(l.unitCost) || 0;
        return sum + qty * cost;
      }, 0),
    [lines],
  );

  function updateLine(i: number, patch: Partial<LineItem>) {
    setLines((ls) =>
      ls.map((l, idx) => {
        if (idx !== i) return l;
        const next = { ...l, ...patch };
        // Al elegir la referencia se precargan sus precios actuales (compra y
        // venta) para poder editarlos desde aquí.
        if (patch.variantId !== undefined) {
          const v = variants.find((x) => x.id === patch.variantId);
          if (v) {
            next.unitCost = String(v.unitCost);
            next.retailPrice = String(v.retailPrice);
          }
        }
        return next;
      }),
    );
  }

  function addLine() {
    setLines((ls) => [...ls, { variantId: '', quantity: '1', unitCost: '', retailPrice: '' }]);
  }
  function removeLine(i: number) {
    setLines((ls) => ls.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const items = lines
      .filter((l) => l.variantId)
      .map((l) => {
        const v = variants.find((x) => x.id === l.variantId)!;
        return {
          variantId: l.variantId,
          productId: v.productId,
          quantity: Math.max(1, Number(l.quantity) || 1),
          unitCost: Math.max(0, Number(l.unitCost) || 0),
          retailPrice: Math.max(0, Number(l.retailPrice) || 0),
        };
      });
    if (items.length === 0) {
      setError('Agrega al menos una referencia.');
      return;
    }

    setSaving(true);
    const { error: saveError } = await savePurchase({ supplierId, invoiceNumber, notes, items });
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title="Nueva Compra"
      subtitle="Registra una factura con cantidades y precios por referencia."
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Proveedor</label>
            <select className={inputCls} value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.companyName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Nº de factura</label>
            <input className={inputCls} value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="FAC-2026-000000" />
          </div>
        </div>

        <div>
          <label className={labelCls}>Referencias</label>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="border border-background-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <select className={`${inputCls} flex-1`} value={l.variantId} onChange={(e) => updateLine(i, { variantId: e.target.value })}>
                    <option value="">Selecciona referencia y talla...</option>
                    {variants.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.productName} · {v.sizeName}
                      </option>
                    ))}
                  </select>
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer shrink-0"
                      aria-label="Quitar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[11px] font-medium text-foreground-500 block mb-1">Cantidad</label>
                    <input type="number" min="1" className={`${inputCls} w-full`} value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-foreground-500 block mb-1">Precio compra ($)</label>
                    <input type="number" min="0" className={`${inputCls} w-full`} value={l.unitCost} onChange={(e) => updateLine(i, { unitCost: e.target.value })} placeholder="0" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-foreground-500 block mb-1">Precio venta ($)</label>
                    <input type="number" min="0" className={`${inputCls} w-full`} value={l.retailPrice} onChange={(e) => updateLine(i, { retailPrice: e.target.value })} placeholder="0" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine} className="mt-2 text-xs text-accent-600 hover:text-accent-700 font-medium cursor-pointer">
            <i className="ri-add-line mr-1"></i>Agregar referencia
          </button>
        </div>

        <div>
          <label className={labelCls}>Notas</label>
          <textarea className={`${inputCls} resize-none`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Detalles de la compra..." />
        </div>

        <div className="flex items-center justify-between bg-accent-50 border border-accent-100 rounded-md px-4 py-3">
          <span className="text-sm text-foreground-700">Costo total</span>
          <span className="text-lg font-bold text-foreground-950">${total.toLocaleString()}</span>
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? (<><i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...</>) : (<><i className="ri-save-line mr-1.5"></i>Crear compra</>)}
          </button>
        </div>
      </form>
    </Modal>
  );
}